/**
 * THE WEB APP'S ANALYTICS CONFIGURATION (#708, ADR 0018; analytics spec, *Where analytics runs*). Everything a build
 * decides about analytics is in this one file.
 *
 * `VITE_GTM_CONTAINER_ID` is read HERE AND NOWHERE ELSE (a source test holds it). Unset, or anything that is not a
 * container id, means nothing ever loads and nothing is asked. `frontend/Dockerfile` sets the owner's container since the
 * turn-on (#713), in the same change as the privacy page's rows (`scripts/sweeps/test_privacy_statements.py` holds the
 * pair), so `main` states nothing the page does not. The browser test makes a build with an id of its own.
 *
 * The web app shares with the landing site a CONTRACT and not code (LP-D18): the cookie below (name, domain, shape,
 * lifetime, `Secure`, `SameSite=Lax`), the container `GTM-5GN5RVX5`, and the Consent Mode order (`loader.ts`). The
 * landing site's side is `landingpage/src/analytics/config.ts`; a change to the contract changes both.
 */

/** The container id this build was made with, or the empty string. */
export const GTM_CONTAINER_ID: string = import.meta.env.VITE_GTM_CONTAINER_ID ?? ''

/** What a container id looks like. Anything else is refused before it can reach an address. */
export const CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]{4,10}$/

/**
 * The hosts analytics may run on, matched EXACTLY against `location.hostname`. The root domain keeps the web app's
 * sign-in pages (`/login`, `/invite/:code`, `/signed-in`, `/first-run`: `api/canonicalHost.ts`), and `app.` is the
 * web app. Never `www.` (Caddy redirects it), never the retired `sport.rieck.pro`, never `localhost`, a demo stack or
 * a developer's machine: their traffic never reaches the production property, and there is no test seam.
 */
export const ANALYTICS_HOSTS: readonly string[] = ['groundwork-sports.com', 'app.groundwork-sports.com']

/**
 * THE VERSION OF THE ANALYTICS TEXT THIS BUILD SHOWS, read by the module from here alone. A yes to an older version
 * loads nothing (Q10), and nothing asks again by itself (CO-D4). Version 2 (#709, AA-D3) also says what the web app
 * sends, so a yes to version 1 does not cover it.
 *
 * A LITERAL, ON A LINE OF ITS OWN, because `scripts/wire-check.mjs` reads it as text. It is one number in four places,
 * tied by `scripts/check_copy_decks.py`: `ANALYTICS_VERSION` in `api/platform/consent.py` (raised first), `ConsentTextVersion.analytics`
 * and `CONSENT_TEXT_VERSION.analytics` in the decks, and this.
 */
export const ANALYTICS_TEXT_VERSION = 2

/** The answer's cookie: `<g|d>.<text version>.<seconds since the epoch>` (analytics spec, *The consent*). */
export const COOKIE_NAME = 'gw_analytics'
/** The registrable domain, so the root domain, `app.` and the landing site read one answer. */
export const COOKIE_DOMAIN = 'groundwork-sports.com'
/** 13 months, as 395 days (Q9): the answer's life, and the `_ga` cookie's life set in the container. */
export const COOKIE_MAX_AGE_SECONDS = 34_128_000

/** After a withdrawal the page reloads with this in its address, once, so the reloaded page can say so (AA-D7). */
export const WITHDRAWN_MARKER = '#cookies-withdrawn'

/** Why the module is off on this page, or null when it may ask and, after a yes, load. */
export function offReason(hostname: string, containerId: string): string | null {
  if (!CONTAINER_ID_PATTERN.test(containerId)) return 'no-container-id'
  if (!ANALYTICS_HOSTS.includes(hostname)) return 'not-production-host'
  return null
}
